import React, {ReactElement, useContext, useEffect, useState} from 'react';
import {Button, Card, Modal, Table, Spin} from 'antd';
import "../../assets/css/Dentobook.css";
import "../../assets/css/PatientPhotos.css";
import {FormattedMessage, useIntl} from "react-intl";
import {DeleteOutlined, DownOutlined, EditOutlined, PlusOutlined, UpOutlined} from "@ant-design/icons";
import ToothComp from "./Tooth";
import type {ColumnsType} from 'antd/es/table';
import PatientTreatmentPlanModal from "./PatientTreatmentPlanModal";
import {
    Appointment, DentalActivityType,
    Patient,
    StatusResponse,
    Tooth, ToothConditionCode,
    ToothStatus,
    ToothTreatment, ToothTreatmentNested, ToothWithRelationships, Treatment
} from "../../api-client";
import dayjs from "dayjs";
import {AlertContext} from "../../context/AlertContext";
import {getPatientApi, getMedicalStaffApi, getTreatmentsApi, getStatusesApi} from "../../api/apiInstances";
// @ts-ignore
import {v4 as uuidv4} from 'uuid';
import {isMobile, isMobileOnly} from "react-device-detect";
import {DentobookApplicationContext} from "../../context/DentobookApplicationContext";
import logger from "../../utils/logger";
import {resolveHistoricalLabel} from "../../utils/historyUtils";
import {ALERT_DURATIONS} from "../../constants/alerts";

interface TreatmentDataType {
    key: string | undefined;
    date: string | undefined;
    condition: string | undefined;
    tooth: string | undefined;
    treatmentId: string | undefined;
    procedure: ReactElement | undefined;
    dental_doctor: ReactElement | undefined;
    sum: ReactElement | undefined;
    discount: ReactElement | undefined;
    total: ReactElement | undefined;
    status: ReactElement | undefined;
    actions: ReactElement | undefined;
    procedures: ProcedureDataType[] | undefined;
    toothPositionId?: string;
}

interface ProcedureDataType {
    key: string | undefined;
    procedure: ReactElement | undefined;
    sum: ReactElement | undefined;
    status: ReactElement | undefined;
}

interface PatientTreatmentPlanCardProps {
    multiSelectionMode: boolean;
    appointment: Appointment;
    patient: Patient;
    teethPositions: ToothWithRelationships[];
    fetchTeeth: Function;
    nzisActivities: DentalActivityType[];
    nzisToothConditionCodes: ToothConditionCode[];
    statuses: StatusResponse[];
}

const PatientTreatmentPlanCard = ({
                                      multiSelectionMode, appointment, nzisActivities, nzisToothConditionCodes,
                                      statuses, patient, teethPositions, fetchTeeth
                                  }: PatientTreatmentPlanCardProps) => {
    const {userOrganization} = useContext(DentobookApplicationContext);
    const currency = userOrganization?.currency ?? 'EUR';

    const PERMANENT_TEETH = 'permanentTeeth';
    const MILK_TEETH = 'milkTeeth';

    const intl = useIntl();

    const {locale} = useContext(DentobookApplicationContext);
    const {setVisible} = useContext(AlertContext);

    const [loading, setLoading] = useState<boolean>(false);

    const [mode, setMode] = useState<'create' | 'edit'>('create');

    const [selectedTeeth, setSelectedTeeth] = useState<ToothWithRelationships[]>([]);

    const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
    const [isTreatmentModalOpen, setIsTreatmentModalOpen] = useState<boolean>(false);
    const [selectedTreatment, setSelectedTreatment] = useState<ToothTreatment | undefined>(undefined);

    const [treatments, setTreatments] = useState<Map<string, Treatment>>();
    const [medicalStatuses, setMedicalStatuses] = useState<Map<string, StatusResponse>>();
    const [teethTreatments, setTeethTreatments] = useState<ToothTreatment[]>([]);
    const [procedures, setProcedures] = useState<Map<string, string>>();

    const [doctors, setDoctors] = useState<Map<string, string>>();

    const [price, setPrice] = useState<string>('00.00');
    const [total, setTotal] = useState<string>('00.00');

    const patientApi = getPatientApi();
    const medicalApi = getMedicalStaffApi();
    const treatmentsApi = getTreatmentsApi();
    const statusesApi = getStatusesApi();

    const statusColors = new Map([
        ['in_progress', 'rgba(152, 200, 255, 1)'],
        ['done', 'rgba(145, 242, 145, 1)'],
        ['canceled', 'rgba(255, 175, 175, 1)'],
        ['offered', 'rgba(245, 229, 120, 1)']
    ]);

    const statusWidths = new Map([
        ['in_progress', 110],
        ['done', 110],
        ['canceled', 110],
        ['offered', 110]
    ]);

    useEffect(() => {
        if (!isTreatmentModalOpen) {
            setSelectedTeeth([]);
        }
    }, [isTreatmentModalOpen]);

    useEffect(() => {
        if (!appointment) {
            return;
        }

        loadData();
    }, []);

    useEffect(() => {
        if (!appointment) {
            return;
        }

        loadData();
    }, [appointment]);

    useEffect(() => {
        // Guard: do not re-open the modal (or switch it to create mode) if it is
        // already open. Without this guard, pressing and releasing Command/Meta
        // while an edit-mode modal is open triggers openTreatmentModal(selectedTeeth,
        // undefined), overwriting mode→'create' and clearing selectedTreatment.
        if (!multiSelectionMode && selectedTeeth.length && !isTreatmentModalOpen) {
            openTreatmentModal(selectedTeeth, undefined);
        }
    }, [multiSelectionMode]);

    const loadData = () => {
        setLoading(true);

        medicalApi.fetchAllProcedures()
            .then(res => {
                const proceduresMap = new Map();
                for (const item of res.data) {
                    proceduresMap.set(item.id!, item.name);
                }
                setProcedures(proceduresMap);

                medicalApi.fetchAllMedicalStaff("", "name", 1, 10000)
                    .then(res => {
                        if (res.data.medicalStaff) {
                            const medicalStaffMap = new Map();
                            for (const item of res.data.medicalStaff) {
                                medicalStaffMap.set(item.id, item.firstName + ' ' + item.lastName);
                            }
                            setDoctors(medicalStaffMap);
                        }

                        treatmentsApi.fetchMedicalTreatments()
                            .then(res => {
                                const treatmentsMap = new Map();

                                for (const item of res.data) {
                                    treatmentsMap.set(item.id, item);
                                }
                                setTreatments(treatmentsMap);

                                statusesApi.fetchAllStatuses()
                                    .then(res => {
                                        const statusesMap = new Map();

                                        for (const item of res.data) {
                                            statusesMap.set(item.id, item);
                                        }
                                        setMedicalStatuses(statusesMap);

                                        patientApi.getAppointmentToothTreatments(appointment.id!)
                                            .then(res => {
                                                setTeethTreatments(res.data);

                                                let priceValue = 0;
                                                let totalValue = 0;
                                                for (const toothTreatment of res.data) {
                                                    // Skip CANCELED treatments from totals calculation
                                                    if (toothTreatment.status?.toUpperCase() === 'CANCELED') {
                                                        continue;
                                                    }

                                                    const treatmentPrice = toothTreatment.price ?? 0;
                                                    const treatmentDiscount = toothTreatment.discount ?? 0;
                                                    const treatmentLabPrice = toothTreatment.laboratoryPrice ?? 0;

                                                    let proceduresPrice = 0;
                                                    let proceduresTotalPrice = 0;

                                                    for (const procedure of toothTreatment.procedures || []) {
                                                        const procPrice = procedure.price ?? 0;
                                                        const procDiscount = procedure.discount ?? 0;
                                                        const procLabPrice = procedure.laboratoryPrice ?? 0;
                                                        proceduresPrice += procPrice;
                                                        proceduresTotalPrice += procPrice - (procPrice * (procDiscount / 100)) + procLabPrice;
                                                    }

                                                    priceValue += treatmentPrice + proceduresPrice;

                                                    const treatmentTotal = treatmentPrice - (treatmentPrice * (treatmentDiscount / 100)) + treatmentLabPrice;
                                                    totalValue += treatmentTotal + proceduresTotalPrice;
                                                }

                                                setPrice(priceValue.toFixed(2));
                                                setTotal(totalValue.toFixed(2));

                                                fetchTeeth()
                                                    .then(() => setLoading(false));
                                            })
                                            .catch(err => logger.log(err));
                                    })
                                    .catch(err => logger.log(err));
                            })
                            .catch(err => logger.log(err));
                    })
            })
            .catch(err => logger.log(err));
    };

    const treatmentColumns: ColumnsType<TreatmentDataType> = [
        {
            title: useIntl().formatMessage({id: 'date'}),
            dataIndex: 'date',
            key: 'date',
            sorter: (dataOne, dataTwo) => {
                const tokensOne = dataOne.date!.split('.');
                const tokensTwo = dataTwo.date!.split('.');
                const firstDate = tokensOne[2] + '-' + tokensOne[1] + '-' + tokensOne[0];
                const secondDate = tokensTwo[2] + '-' + tokensTwo[1] + '-' + tokensTwo[0];

                return firstDate.localeCompare(secondDate);
            },
            width: '10%'
        },
        {
            title: useIntl().formatMessage({id: 'condition'}),
            dataIndex: 'condition',
            key: 'condition',
            width: '25%'
        },
        {
            title: useIntl().formatMessage({id: 'tooth'}),
            dataIndex: 'tooth',
            key: 'tooth',
            sorter: (dataOne, dataTwo) => {
                if (!dataOne.tooth || !dataTwo.tooth) {
                    return 1;
                }

                return dataOne.tooth.localeCompare(dataTwo.tooth);
            },
            width: '10%'
        },
        {
            title: useIntl().formatMessage({id: 'operation'}),
            dataIndex: 'procedure',
            key: 'procedure',
            width: '20%'
        },
        {
            title: useIntl().formatMessage({id: 'sum'}),
            dataIndex: 'sum',
            key: 'sum',
            width: '10%'
        },
        {
            title: useIntl().formatMessage({id: 'discount_label'}),
            dataIndex: 'discount',
            key: 'discount',
            width: '10%'
        },
        {
            title: useIntl().formatMessage({id: 'total'}),
            dataIndex: 'total',
            key: 'total',
            width: '10%'
        },
        {
            title: useIntl().formatMessage({id: 'status'}),
            dataIndex: 'status',
            key: 'status',
            width: '14%'
        },
        {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            width: '11%'
        },
    ];

    const procedureColumns: ColumnsType<ProcedureDataType> = [
        {
            title: useIntl().formatMessage({id: 'procedure'}),
            dataIndex: 'procedure',
            key: 'procedure'
        },
        {
            title: useIntl().formatMessage({id: 'sum'}),
            dataIndex: 'sum',
            key: 'sum',
            width: 120
        },
        {
            title: useIntl().formatMessage({id: 'status'}),
            dataIndex: 'status',
            key: 'status',
            width: 240
        },
    ];

    const data: TreatmentDataType[] = teethTreatments.map((toothTreatment, index) => {
        toothTreatment.procedures = toothTreatment.procedures?.map(pr => {
            return {...pr, uid: uuidv4()};
        });

        const treatmentObject = treatments?.get(toothTreatment.treatmentId!);
        const treatmentDisplayName = resolveHistoricalLabel({ snapshot: toothTreatment.treatmentNameSnapshot, entity: treatmentObject });

        const statusEntity = toothTreatment.statusId ? medicalStatuses?.get(toothTreatment.statusId) : undefined;
        const conditionLabel = resolveHistoricalLabel({
            snapshot: toothTreatment.statusNameSnapshot,
            entity: statusEntity,
            entityLabelField: 'displayName',
            fallback: '',
        });

        const proceduresArr = toothTreatment.procedures?.map(procedureItem => {

            const procedureName = procedures?.get(procedureItem.procedureId!);

            const statusKey = procedureItem.status ? procedureItem.status.toLowerCase() : '';
            const color = statusKey ? statusColors.get(statusKey) : undefined;

            return {
                key: uuidv4(),
                condition: conditionLabel || undefined,
                tooth: toothTreatment.tooth?.position?.toString(),
                treatmentId: toothTreatment.id,
                procedure: <div>{procedureName}</div>,
                sum: <div>{(procedureItem.price?.toFixed(2) || '00.00') + ' ' + currency}</div>,
                status: statusKey ? <div style={{
                    width: statusWidths.get(statusKey),
                    background: color,
                    borderRadius: 6,
                    padding: 4,
                    textAlign: 'center'
                }}>
                    <FormattedMessage id={statusKey}/></div> : <div>-</div>
            };
        });

        const toothPosition = teethPositions.filter(toothPos => toothPos.id === toothTreatment.tooth?.id)[0];

        const doctor = doctors?.get(toothTreatment.medicalStaffId!);

        const treatmentStatusKey = toothTreatment.status ? toothTreatment.status.toLowerCase() : '';
        const color = treatmentStatusKey ? statusColors.get(treatmentStatusKey) : undefined;

        // Calculate total with discount
        const treatmentPrice = toothTreatment.price ?? 0;
        const treatmentDiscount = toothTreatment.discount ?? 0;
        const laboratoryPrice = toothTreatment.laboratoryPrice ?? 0;
        const treatmentTotal = treatmentPrice - (treatmentPrice * (treatmentDiscount / 100)) + laboratoryPrice;

        return {
            key: uuidv4(),
            condition: conditionLabel || undefined,
            tooth: toothTreatment.tooth?.position?.toString(),
            treatmentId: toothTreatment.id,
            procedure: <div>
                {treatmentDisplayName}
            </div>,
            dental_doctor: <div>{doctor}</div>,
            date: dayjs(toothTreatment.date).utc().format('DD.MM.YYYY'),
            sum: <div>{treatmentPrice.toFixed(2) + ' ' + currency}</div>,
            discount: <div>{treatmentDiscount + '%'}</div>,
            total: <div>{treatmentTotal.toFixed(2) + ' ' + currency}</div>,
            status: treatmentStatusKey ? <div style={{
                width: statusWidths.get(treatmentStatusKey),
                background: color,
                borderRadius: 6,
                padding: 4,
                textAlign: 'center'
            }}><FormattedMessage
                id={treatmentStatusKey}/></div> : <div>-</div>,
            procedures: proceduresArr,
            actions: <div>
                <Button className="btn-no-border"
                        style={{padding: 10}}
                        ghost={false} size="large" onClick={(event) => {
                    event.stopPropagation();
                    openTreatmentModal(!toothPosition ? [] : [toothPosition], toothTreatment);
                }}><EditOutlined/></Button>
                {' '}
                <Button className="btn-no-border"
                        style={{padding: 10}}
                        ghost={false} size="large" onClick={(event) => {
                    event.stopPropagation();
                    setSelectedTreatment(toothTreatment);
                    setConfirmModalOpen(true);
                }}><DeleteOutlined/></Button>
            </div>,
            toothPositionId: toothPosition ? (toothPosition.id ? toothPosition.id : '') : '',
        };
    });


    const tabList = [
        {
            key: PERMANENT_TEETH,
            tab: useIntl().formatMessage({id: 'permTeeth'}),
        },
        {
            key: MILK_TEETH,
            tab: useIntl().formatMessage({id: 'milkTeeth'}),
        },
    ];

    const [activeTabKey, setActiveTabKey] = useState<string>(PERMANENT_TEETH);

    const openTreatmentModal = (teethPositions: ToothWithRelationships[], toothTreatment: ToothTreatment | undefined) => {
        setSelectedTeeth(teethPositions);

        setSelectedTreatment(toothTreatment || undefined);

        if (toothTreatment) {
            setMode('edit');
        } else {
            setMode('create');
        }

        setIsTreatmentModalOpen(true);
    }

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '100px 0' }}>
                <Spin tip={intl.formatMessage({ id: 'loading' })} />
            </div>
        );
    }
    const expandedRow = (row: TreatmentDataType) => {
        if (!row.procedures!.length) {
            return null;
        }

        return <Table showHeader={false} columns={procedureColumns} dataSource={row.procedures} pagination={false}/>;
    };

    const handleConfirmOk = () => {
        if (!appointment) {
            logger.log("appointment id is undefined.....");
            return;
        }

        if (!selectedTreatment?.id) return;

        patientApi.deleteToothTreatment(appointment.id!, selectedTreatment.id)
            .then(() => {
                setVisible(true);
                setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);

                loadData();
            })
            .catch(err => logger.log(err));

        setConfirmModalOpen(false);
    };

    const handleConfirmCancel = () => {
        setSelectedTreatment(undefined);

        setConfirmModalOpen(false);
    };

    const tableFooter = () => {
        const priceNum = parseFloat(price) || 0;
        const totalNum = parseFloat(total) || 0;
        const discountAmount = priceNum - totalNum;

        return (
            <div className="dentobook-finances-table-footer">
                <span><FormattedMessage id="totalPrice"/>: {price} {currency}</span>
                <span><FormattedMessage id="discount_label"/>: {discountAmount.toFixed(2)} {currency}</span>
                <strong><FormattedMessage id="totalTwo"/>: {total} {currency}</strong>
            </div>
        );
    };

    return (
        <>
            <Modal
                transitionName=""
                okText={<FormattedMessage id="confirm"/>}
                className="dentobook-modal"
                okButtonProps={{
                    className: "modal-btn"
                }}
                cancelButtonProps={{
                    className: "modal-btn"
                }}
                cancelText={<FormattedMessage id="cancel"/>}
                open={isConfirmModalOpen} onOk={handleConfirmOk} onCancel={handleConfirmCancel}>
                <br/>
                <strong><FormattedMessage id="tooth_treatment_delete_confirm"/></strong>
            </Modal>
            <PatientTreatmentPlanModal appointment={appointment.id!}
                                       mode={mode}
                                       patient={patient}
                                       statuses={statuses}
                                       selectedTreatment={selectedTreatment!}
                                       loadData={loadData}
                                       allTeethPositions={teethPositions}
                                       toothObjs={selectedTeeth}
                                       isModalOpen={isTreatmentModalOpen}
                                       setIsModalOpen={setIsTreatmentModalOpen}
                                       title={selectedTeeth.map(tooth => tooth?.position?.toString()).join(', ')}/>
            <Card tabList={tabList}
                  rootClassName="patient-treatment"
                  defaultActiveTabKey={activeTabKey}
                  onTabChange={key => setActiveTabKey(key)}
                  tabBarExtraContent={<Button shape="round"
                                              className="dentobook-btn-secondary dentobook-btn-medium color-primary"
                                              type={"default"}
                                              onClick={() => openTreatmentModal([], undefined)}><PlusOutlined/>
                      <FormattedMessage
                          id="add"/></Button>}
                  style={{
                      overflow: isMobileOnly ? 'scroll' : 'initial',
                      height: 'auto',
                      marginBottom: isMobile ? 200 : 80
                  }}>
                <div>
                    <div className={activeTabKey === 'milkTeeth' ? "milk-teeth-card" : "permanent-teeth-card"}>
                        {teethPositions
                            .filter(toothPos => {
                                if (activeTabKey === PERMANENT_TEETH) return toothPos.position && toothPos.position < 50;
                                else return toothPos.position && toothPos.position > 50;
                            })
                            .map(toothPosition => {
                                let tooltipMessage;
                                let color;

                                let localTreatmentCount = 0;
                                let toothTreatment;

                                if (toothPosition.toothTreatments && toothPosition.toothTreatments.length > 0) {
                                    toothTreatment = toothPosition.toothTreatments[0];
                                    if (toothPosition.toothTreatments.length > 1) {
                                        tooltipMessage = (
                                            <div>
                                                {toothPosition.toothTreatments.map((treatment: ToothTreatmentNested, index: number) => {
                                                    const treatmentName = resolveHistoricalLabel({ snapshot: treatment.treatmentNameSnapshot, entity: treatment.treatmentId ? treatments?.get(treatment.treatmentId) : undefined });
                                                    if (treatment.treatmentId != null) localTreatmentCount++;
                                                    color = statusColors.get(treatment.status!.toLowerCase());
                                                    return <div
                                                        key={treatment.id}>{`${intl.formatMessage({id: 'treatment_label'})}: ${treatmentName}`}</div>;
                                                })}
                                            </div>
                                        );
                                    } else {
                                        // Handle a single treatment.
                                        toothTreatment = toothPosition.toothTreatments[0];
                                        const treatmentName = resolveHistoricalLabel({ snapshot: toothTreatment.treatmentNameSnapshot, entity: toothTreatment.treatmentId ? treatments?.get(toothTreatment.treatmentId) : undefined });
                                        if (toothTreatment.treatmentId != null) localTreatmentCount++;
                                        color = statusColors.get(toothTreatment.status!.toLowerCase());
                                        tooltipMessage = `${intl.formatMessage({id: 'treatment_label'})}: ${treatmentName}`;
                                    }
                                } else {
                                    tooltipMessage = intl.formatMessage({id: 'no_treatment'});
                                }

                                const toothStatusesLength = toothPosition.toothStatuses?.length!;

                                const toothStatus = toothPosition.toothStatuses?.sort((itemOne: ToothStatus, itemTwo: ToothStatus) => itemOne.createdAt?.localeCompare(itemTwo.createdAt!)!)[toothStatusesLength - 1];
                                let statusObject: StatusResponse | undefined;
                                if (toothStatus && toothPosition.toothTreatments?.length === 0) {
                                    statusObject = statuses.find(statusObj => statusObj.id === toothStatus.statusId);
                                }

                                let showToothStatus = toothPosition.toothTreatments?.length === 0
                                    && toothPosition.toothStatuses?.length! > 0;

                                let statusLabel = '';
                                if (showToothStatus) {
                                    statusLabel = statusObject?.label ?? '';
                                } else if (!showToothStatus && toothPosition.toothTreatments?.length! > 0) {
                                    statusLabel = toothPosition.toothTreatments?.length.toString() ?? '';
                                }

                                return (
                                    <button key={toothPosition.id}
                                            className="plain-button"
                                            onKeyDown={e => e.preventDefault()}
                                            onClick={() => {
                                                if (multiSelectionMode && selectedTeeth.indexOf(toothPosition) === -1) {
                                                    setSelectedTeeth(selectedTeeth.concat(toothPosition));
                                                } else if (multiSelectionMode && selectedTeeth.indexOf(toothPosition) > -1) {
                                                    setSelectedTeeth(selectedTeeth.filter(tooth => tooth.id !== toothPosition.id));
                                                } else {
                                                    setSelectedTeeth([]);
                                                    openTreatmentModal([toothPosition], undefined);
                                                }
                                            }}>
                                        <ToothComp position={toothPosition.position || 0}
                                                   statusLabel={statusLabel}
                                                   statusColor={showToothStatus ? (statusObject?.color ?? undefined) : (color ?? undefined)}
                                                   headerColor={showToothStatus ? undefined : (color ?? undefined)}
                                                   milk={activeTabKey === 'milkTeeth'}
                                                   selected={multiSelectionMode && selectedTeeth.indexOf(toothPosition) > -1}
                                                   multiSelectionMode={multiSelectionMode}
                                                   key={toothPosition.id}
                                                   tooltipContent={tooltipMessage}
                                        />
                                    </button>
                                );
                            })}
                    </div>
                    <Table
                        locale={{emptyText: intl.formatMessage({id: 'no_data'})}}
                        footer={tableFooter}
                        className={(activeTabKey === 'milkTeeth' ? "milk-teeth-table" : "permanent-teeth-table") + " teeth-table pt-2"}
                        columns={treatmentColumns}
                        dataSource={data.filter(item => item.actions)}
                        expandable={{
                            expandIcon: (props) => {
                                if (!props.expandable) {
                                    return null;
                                }
                                if (props.expanded) {
                                    return <button style={{color: 'black'}}
                                                   className="plain-button"
                                                   onKeyDown={e => e.preventDefault()}
                                                   onClick={e => {
                                                       props.onExpand(props.record, e);
                                                   }}><DownOutlined/></button>
                                }
                                return <button style={{color: 'black'}}
                                               className="plain-button"
                                               onKeyDown={e => e.preventDefault()}
                                               onClick={e => {
                                                   props.onExpand(props.record, e);
                                               }}><UpOutlined/></button>
                            },
                            expandedRowRender: expandedRow,
                            rowExpandable: (record) => record.procedures!.length > 0
                        }} pagination={false}
                        onRow={(record: TreatmentDataType) => ({
                            onClick: () => {
                                const toothPosition = teethPositions.find(toothPosition => toothPosition.id === record.toothPositionId);
                                const toothTreatment = teethTreatments.find(toothTreatment => toothTreatment.id === record.treatmentId);
                                openTreatmentModal(!toothPosition ? [] : [toothPosition], toothTreatment);
                            },
                        })}
                    />
                </div>
            </Card>
        </>
    );
};

export default PatientTreatmentPlanCard;